import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import LoadingStatus from "@imminently/decisively-ui/LoadingStatus";
import { useEventResolutionEnabled } from "@/features/events/hooks/useEventResolutionEnabled";
import { StatusLabel } from "@/features/tasks/TaskComponents";
import { EventConnectorStatus } from "@/features/events/components/EventConnectorStatus";
import Tabs from "@imminently/decisively-ui/Tabs";
import { EVENT_TYPE_INFO } from "@packages/commons";
import { Activity, AlertCircle, CheckCircle2, CircleCheck, MousePointer2 } from "lucide-react";
import { EventListContent } from "@/features/events/components/EventListContent";
import { EventDetails } from "@/features/events/components/EventDetails";
import { EventListSkeleton } from "@/features/events/components/EventListSkeleton";
import { EventTypeFilter } from "@/features/events/components/EventTypeFilter";
import { EventTypesButton } from "@/features/events/components/EventTypesButton";
import { eventDetailsUrl, readEventDetails } from "@/features/events/utils/eventDetails";
import { global } from "@/global";
import { EventConnectorDialog } from "@/features/events/components/EventConnectorDialog";
import { type EventConnector, findConnectors } from "@/pages/admin/Settings/settings.api";
import { useScope } from "@/redux/scope";
import Button from "@imminently/decisively-ui/Button";
import DataTable, { type DataTableColumn } from "@imminently/decisively-ui/DataTable";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { EventIcon } from "@/features/events/components/EventIcon";
import Chip from "@imminently/decisively-ui/Chip";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import Icon from "@imminently/decisively-ui/Icon";
import ItemList from "@imminently/decisively-ui/ItemList";
import DeciPopover from "@imminently/decisively-ui/Popover";
import SelectField from "@imminently/decisively-ui/SelectField";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import TextField from "@imminently/decisively-ui/TextField";
import { Typography } from "@mui/material";
import { getEventType, getEventTypeDisplayName, getEventTypeInfo } from "@packages/commons";
import moment from "moment";
import React from "react";
import { useInfiniteQuery, useQuery, useQueries } from "react-query";
import { Redirect, useHistory, useLocation } from "react-router-dom";


interface EventEntry {
  id?: string;
  eventRowKey?: string;
  eventType?: string;
  status?: string;
  timestamp?: string;
  modelId?: string;
  [key: string]: any;
}

const PAGE_SIZE = 10;

type EventsSource = "tenancy" | "model";

interface EventsProps {
  source?: EventsSource;
}

function getDisplayEventType(entry?: EventEntry | null): string {
  return entry?.eventType || "";
}

function normalizeConnectorsResponse(raw: any): EventConnector[] {
  const data = raw?.data ?? raw;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function getStatusChipVariant(status?: string): React.ComponentProps<typeof Chip>["variant"] {
  if (status === "success") return "success";
  if (status === "error") return "destructive";
  if (status === "warning") return "warning";
  return "secondary";
}

function normalizePage(raw: any): EventEntry[] {
  return Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.data?.data)
      ? (raw as any).data.data
      : Array.isArray((raw as any)?.data)
        ? (raw as any).data
        : [];
}

function parseDayFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  const day = params.get("day");
  if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) return day;
  return "";
}

function parseStatusFromSearch(search: string): "" | "success" | "warning" | "error" | "info" | "debug" {
  const params = new URLSearchParams(search);
  const s = params.get("status") || params.get("status");
  if (s === "success" || s === "warning" || s === "error" || s === "info" || s === "debug") return s;
  return "";
}

function parseCorrelationIdFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  return params.get("correlationId") || "";
}

function parseEventIdFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  return params.get("eventId") || "";
}

function parseConnectorEventFromSearch(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const eventType = params.get("connectorEvent")?.trim();
  return eventType ? getEventType(eventType) || eventType : undefined;
}

function parseStartFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  return params.get("start") || "";
}

function parseEndFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  return params.get("end") || "";
}

type TimeRangeType = "none" | "last5min" | "lastHour" | "last24h" | "custom";

function buildSearch(
  day: string,
  status: "" | "success" | "warning" | "error" | "info" | "debug",
  correlationId: string,
  start?: string,
  end?: string,
  eventType?: string,
): string {
  const params = new URLSearchParams();
  if (day) params.set("day", day);
  if (eventType) params.set("eventType", eventType);
  if (status) params.set("status", status);
  if (correlationId) params.set("correlationId", correlationId);
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  const q = params.toString();
  return q ? `?${q}` : "";
}

function getTimeRangeStartEnd(
  day: string,
  timeRangeType: TimeRangeType,
  customStart?: string,
  customEnd?: string,
): { start?: string; end?: string } {
  const dayStart = day ? moment.utc(day).startOf("day") : undefined;
  const dayEnd = day ? moment.utc(day).endOf("day") : undefined;
  const now = moment.utc();

  switch (timeRangeType) {
    case "last5min":
      return {
        start: now.clone().subtract(5, "minutes").toISOString(),
        end: now.toISOString(),
      };
    case "last24h":
      return { start: now.clone().subtract(24, "hours").toISOString(), end: now.toISOString() };
    case "lastHour":
      return {
        start: now.clone().subtract(1, "hour").toISOString(),
        end: now.toISOString(),
      };
    case "custom":
      if (customStart && customEnd) {
        // Parse the datetime-local format (YYYY-MM-DDTHH:mm) as UTC
        // The input is already in UTC timezone format, just needs to be parsed correctly
        let startMoment = moment.utc(customStart, "YYYY-MM-DDTHH:mm", true);
        let endMoment = moment.utc(customEnd, "YYYY-MM-DDTHH:mm", true);

        // When a day is selected, keep custom times inside that UTC day.
        if (dayStart && startMoment.isBefore(dayStart)) {
          startMoment = dayStart.clone();
        }
        if (dayEnd && startMoment.isAfter(dayEnd)) {
          startMoment = dayEnd.clone();
        }
        if (dayEnd && endMoment.isAfter(dayEnd)) {
          endMoment = dayEnd.clone();
        }
        if (dayStart && endMoment.isBefore(dayStart)) {
          endMoment = dayStart.clone();
        }
        // Ensure start is before end
        if (startMoment.isAfter(endMoment)) {
          const temp = startMoment;
          startMoment = endMoment;
          endMoment = temp;
        }

        return {
          start: startMoment.toISOString(),
          end: endMoment.toISOString(),
        };
      }
      return {};
    default:
      return {};
  }
}

export const Events = ({ source = "tenancy" }: EventsProps) => {
  const location = useLocation();
  const history = useHistory();
  const { project: modelId } = useScope();
  const params = new URLSearchParams(location.search);
  const requestedTab = params.get("tab");
  const tab = requestedTab === "events" || requestedTab === "overview"
    ? requestedTab : (params.has("eventId") || params.has("eventType") || params.has("group") ? "events" : "overview");
  const overview = tab === "overview";
  const resolutionEnabled = useEventResolutionEnabled();
  const group = params.get("group") || "";
  const resolution = !resolutionEnabled ? "" : overview ? (params.get("resolution") === "resolved" ? "resolved" : "open") : params.get("resolution") || "";
  const tenancyId = localStorage.getItem("decisively.tenant");
  const navigateView = (updates: Record<string, string>) => {
    const next = new URLSearchParams(location.search);
    next.delete("eventId");
    for (const [key, value] of Object.entries(updates)) value ? next.set(key, value) : next.delete(key);
    setSelectedEventId(null);
    history.push({ pathname: location.pathname, search: next.toString() });
  };
  const day = parseDayFromSearch(location.search);
  const status = overview ? "error" : parseStatusFromSearch(location.search);
  const eventType = new URLSearchParams(location.search).get("eventType") || "";
  // Debug events (e.g. job started/completed) are left out unless the toggle is on.
  const showDebug = params.get("debug") === "true";
  const correlationId = parseCorrelationIdFromSearch(location.search);
  const eventIdFromSearch = parseEventIdFromSearch(location.search);
  const [connectorSetupEvent, setConnectorSetupEvent] = React.useState<string | undefined>(() => parseConnectorEventFromSearch(location.search));
  const start = parseStartFromSearch(location.search);
  const end = parseEndFromSearch(location.search);
  const rangeStart = start;
  const rangeEnd = end;

  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null);
  const [overviewSearch, setOverviewSearch] = React.useState("");
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [filterDay, setFilterDay] = React.useState(day);
  const [filterStatus, setFilterStatus] = React.useState(status);
  const [filterEventType, setFilterEventType] = React.useState(eventType);
  const [filterCorrelationId, setFilterCorrelationId] = React.useState(correlationId);
  const [filterTimeRangeType, setFilterTimeRangeType] = React.useState<TimeRangeType>(start || end ? "custom" : "none");
  const [filterCustomStart, setFilterCustomStart] = React.useState(
    start ? moment.utc(start).format("YYYY-MM-DDTHH:mm") : "",
  );
  const [filterCustomEnd, setFilterCustomEnd] = React.useState(end ? moment.utc(end).format("YYYY-MM-DDTHH:mm") : "");
  const [connectors, setConnectors] = React.useState<EventConnector[]>([]);

  const buildEventsUrl = (cursor?: string, recentActivity = false) => {
    const params = new URLSearchParams();
    params.set("$limit", String(PAGE_SIZE));
    if (cursor) params.set("cursor", cursor);
    if (group) params.set("group", group);
    if (resolution && !recentActivity) params.set("resolution", resolution);
    if (eventType) params.set("eventType", eventType);
    if (status && !recentActivity) params.set("status", status);
    if (showDebug) params.set("includeDebug", "true");
    if (source === "tenancy") {
      if (day) params.set("day", day);
      if (correlationId) params.set("correlationId", correlationId);
      if (rangeStart) params.set("start", rangeStart);
      if (rangeEnd) params.set("end", rangeEnd);
      return `auth/events?${params.toString()}`;
    }

    if (day) params.set("day", day);
    if (rangeStart) params.set("start", rangeStart);
    if (rangeEnd) params.set("end", rangeEnd);
    return `models/${modelId}/events?${params.toString()}`;
  };

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    [
      source,
      "events",
      "event-list",
      tenancyId, tab, group, resolution, showDebug,
      modelId || null,
      day,
      status || null,
      eventType || null,
      correlationId || null,
      rangeStart || null,
      rangeEnd || null,
    ],
    ({ pageParam }) => {
      const page = pageParam;
      return global.client.get(buildEventsUrl(page));
    },
    {
      enabled: requestedTab !== "files" && (source === "tenancy" || Boolean(modelId)),
      retry: false,
      getNextPageParam: (lastPage: any) => lastPage?.nextCursor ?? lastPage?.data?.nextCursor,
    },
  );

  const metricFilters = [
    { status: "error", resolution: resolutionEnabled ? "open" : "" },
    { status: "error", resolution: "resolved" },
    { status: "success", resolution: "" },
    { status: "", resolution: "" },
  ];
  const metricQueries = useQueries(metricFilters.map((filter, index) => ({
    queryKey: [source, "events", "overview-metric", tenancyId, modelId || null, group, eventType, day, correlationId, rangeStart, rangeEnd, filter],
    queryFn: () => {
      const url = buildEventsUrl(undefined, true);
      const [path, search] = url.split("?");
      const query = new URLSearchParams(search);
      if (filter.status) query.set("status", filter.status);
      if (filter.resolution) query.set("resolution", filter.resolution);
      if (!filter.status) query.set("$limit", "1");
      return global.client.get(`${path}?${query}`);
    },
    enabled: overview && (index !== 1 || resolutionEnabled) && (source === "tenancy" || Boolean(modelId)),
    retry: false,
  })));
  const latestEvent = normalizePage(metricQueries[3].data)[0];
  const metricCount = (index: number) => {
    const result: any = metricQueries[index].data;
    const count = normalizePage(result).length;
    const more = result?.nextCursor || result?.data?.nextCursor;
    return more ? (count ? `${count}+` : "View history") : String(count);
  };
  const overviewCards = [
    { title: "Needs attention", icon: AlertCircle, value: metricCount(0), description: resolutionEnabled ? "Unresolved errors" : "Error events", tone: "text-destructive",
      open: () => navigateView({ tab: "overview", resolution: "open", status: "" }) },
    { title: "Resolved", icon: CircleCheck, value: metricCount(1), description: "Reviewed errors", tone: "text-textSecondary",
      open: () => navigateView({ tab: "overview", resolution: "resolved", status: "" }) },
    { title: "Successful", icon: CheckCircle2, value: metricCount(2), description: "Successful outcomes", tone: "text-primary",
      open: () => navigateView({ tab: "events", resolution: "", status: "success" }) },
    { title: "Latest event", icon: Activity,
      value: latestEvent?.timestamp ? moment(latestEvent.timestamp).fromNow() : latestEvent ? "Unknown time" : "None",
      description: latestEvent ? getEventTypeDisplayName(getDisplayEventType(latestEvent)) : "No activity",
      tone: "text-textSecondary",
      open: () => {
        const id = latestEvent?.eventRowKey || latestEvent?.id;
        navigateView({ tab: "events", resolution: "", status: "", ...(id ? { eventId: id } : {}) });
      } },
  ];

  const {
    data: serverEventData,
    isLoading: isFetchingEvent,
    error: eventError,
  } = useQuery(
    [source, "events", selectedEventId, modelId || null, tenancyId],
    () => global.client.get(eventDetailsUrl(source, selectedEventId || "", modelId)).then(readEventDetails),
    { enabled: Boolean(selectedEventId) && (source === "tenancy" || Boolean(modelId)), retry: false },
  );

  React.useEffect(() => { setSelectedEventId(null); }, [modelId, tenancyId, source]);

  const entries: EventEntry[] = React.useMemo(() => (data?.pages ?? []).flatMap(normalizePage), [data?.pages]);
  const title = overview ? (resolution === "resolved" ? "Resolved errors" : "Needs attention") : "Events";
  const activeFilterCount = [!overview && status, eventType, correlationId, start && end].filter(Boolean).length;

  React.useEffect(() => {
    if (eventIdFromSearch) setSelectedEventId(eventIdFromSearch);
  }, [eventIdFromSearch]);

  React.useEffect(() => {
    const tenancyId = localStorage.getItem("decisively.tenant");
    if (!tenancyId) {
      setConnectors([]);
      return;
    }

    findConnectors
      .submit({ params: { id: tenancyId } })
      .then((response) => setConnectors(normalizeConnectorsResponse(response)))
      .catch(() => setConnectors([]));
  }, [connectorSetupEvent]);

  const handleEventSelect = (entry: EventEntry) => {
    const id = entry.id || entry.eventRowKey;
    if (id) setSelectedEventId(String(id));
  };

  const openConnectorSetup = (eventId: string) => {
    setConnectorSetupEvent(eventId);
  };

  const closeConnectorSetup = () => {
    setConnectorSetupEvent(undefined);
  };

  const handleApplyFilters = () => {
    const timeRange = getTimeRangeStartEnd(filterDay, filterTimeRangeType, filterCustomStart, filterCustomEnd);

    history.push({
      pathname: location.pathname,
      search: (buildSearch(
        filterDay,
        filterStatus,
        filterCorrelationId,
        timeRange.start,
        timeRange.end,
        filterEventType,
      ) || "?") + `&tab=${tab}${group ? `&group=${encodeURIComponent(group)}` : ""}${resolution ? `&resolution=${resolution}` : ""}${showDebug ? "&debug=true" : ""}`,
    });
    setFiltersOpen(false);
  };

  const openFilterPopover = () => {
    setFilterDay(day);
    setFilterStatus(status);
    setFilterEventType(eventType);
    setFilterCorrelationId(correlationId);
    setFilterTimeRangeType(start || end ? "custom" : "none");
    setFilterCustomStart(start ? moment.utc(start).format("YYYY-MM-DDTHH:mm") : "");
    setFilterCustomEnd(end ? moment.utc(end).format("YYYY-MM-DDTHH:mm") : "");
  };

  const handleTimeRangeTypeChange = (newType: TimeRangeType) => {
    setFilterTimeRangeType(newType);
    if (newType !== "custom") {
      setFilterCustomStart("");
      setFilterCustomEnd("");
    }
  };

  const renderOverviewTable = () => {
    if (error) return <p role="alert" className="p-4 text-sm text-destructive">Unable to load events: {(error as Error).message}</p>;
    const search = overviewSearch.trim().toLowerCase();
    const rows = entries.filter(entry => !search || [
      entry.message, getEventTypeInfo(entry.eventType)?.name, entry.eventType,
      ...(entry.facts || []).map((fact: any) => String(fact.value)),
    ].some(value => String(value || "").toLowerCase().includes(search)));
    const columns: DataTableColumn[] = [
      { field: "status", headerName: "Status", width: 90, sortable: false,
        renderCell: ({ row }) => resolutionEnabled
          ? (row.resolution?.taskId && row.resolution.status ? <StatusLabel status={row.resolution.status} /> : null)
          : <Chip size="sm" variant="destructive" label="Error" /> },
      { field: "eventType", headerName: "Event", flex: 1.2, minWidth: 160, sortable: false,
        renderCell: ({ row }) => {
          const info = getEventTypeInfo(row.eventType);
          return <Tooltip><Tooltip.Trigger asChild>
            <span className="flex min-w-0 items-center gap-2" tabIndex={0}>
              <EventIcon eventType={row.eventType} size={17} className="shrink-0 text-textSecondary" />
              <span className="truncate font-medium">{getEventTypeDisplayName(row.eventType)}</span>
            </span>
          </Tooltip.Trigger><Tooltip.Content placement="bottom">
            <code className="block text-xs">{row.eventType}</code><p className="max-w-sm text-sm">{info?.description}</p>
          </Tooltip.Content></Tooltip>;
        } },
      { field: "group", headerName: "Group", width: 95, sortable: false,
        renderCell: ({ row }) => {
          const category = getEventTypeInfo(row.eventType)?.category;
          return <span className="capitalize">{category === "fileProcessing" ? "File processing" : category === "orgmodels" ? "Org models" : category || "Custom"}</span>;
        } },
      { field: "message", headerName: "Message", flex: 1.5, minWidth: 140, sortable: false,
        renderCell: ({ row }) => <span className="truncate" title={row.message}>{row.message || "No message"}</span> },
      { field: "timestamp", headerName: "When", width: 100, sortable: false,
        renderCell: ({ row }) => <span title={row.timestamp}>{row.timestamp ? moment(row.timestamp).fromNow() : "Unknown"}</span> },
    ];
    return <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1">
      <DataTable rows={rows} columns={columns} getRowId={row => row.id || row.eventRowKey}
        loading={isLoading} rowHeight={48} columnHeaderHeight={36} hideFooter pagination={false}
        disableColumnMenu disableRowSelectionOnClick
        onRowClick={({ row }) => setSelectedEventId(row.id || row.eventRowKey)}
        getRowClassName={({ row }) => (row.id || row.eventRowKey) === selectedEventId ? "selected-event" : ""}
        className="min-h-0 flex-1 border-0 [&_.selected-event]:!bg-primary-subtle [&_.selected-event]:!border-l-2 [&_.selected-event]:!border-l-primary"
        onRowsScrollEnd={() => { if (hasNextPage && !isFetchingNextPage) void fetchNextPage(); }}
        slots={{ noRowsOverlay: () => (
          <div className="flex h-full flex-col items-center justify-center px-6 pb-10 text-center">
            <CheckCircle2 size={28} className="mb-3 text-textSecondary" />
            <p className="text-sm font-medium">{search ? "No matching events" : hasNextPage ? "No matches on this page" : resolution === "open" ? "No errors need attention" : resolutionEnabled ? "No resolved errors" : "No errors found"}</p>
            <p className="mt-2 max-w-sm text-xs leading-relaxed text-textSecondary">{hasNextPage ? "Load older events to continue through the history." : search ? "Try another search or clear the current filters." : "Try a different group or change the filters to explore other activity."}</p>
          </div>
        ) }}
      />
      </div>
      {hasNextPage && <div className="border-t border-border px-4 py-2">
        <Button size="sm" variant="ghost" label={isFetchingNextPage ? "Loading…" : "Load older events"}
          disabled={isFetchingNextPage} onClick={() => fetchNextPage()} />
      </div>}
    </div>;
  };

  const renderEventsList = () => {
    if (error) {
      return (
        <Typography
          variant="body2"
          color="error"
          style={{ padding: "1rem" }}
        >
          Error loading events: {(error as Error).message || "Unknown error"}
        </Typography>
      );
    }

    if (entries.length === 0 && hasNextPage) {
      return <div className="p-4">
        <p>No matches in this page.</p>
        <Button label="Search older events" disabled={isFetchingNextPage} onClick={() => fetchNextPage()} />
      </div>;
    }

    if (entries.length === 0 && !isLoading) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ padding: "1rem" }}
        >
          No events found.
        </Typography>
      );
    }

    return (
      <>
        <ItemList
          className="h-full min-h-0 flex-1"
          items={entries}
          aria-busy={isLoading || isFetchingNextPage}
          skeletonRows={isLoading ? 3 : isFetchingNextPage ? 2 : 0}
          renderSkeletonItem={() => <EventListSkeleton />}
          getItemKey={(entry, index) => String(entry.id || entry.eventRowKey || index)}
          renderItem={(entry, index) => {
            const id = String(entry.id || entry.eventRowKey || index);
            const isSelected = selectedEventId === id;
            const timestamp = entry.timestamp ? moment(entry.timestamp).format("YYYY-MM-DD HH:mm:ss") : "N/A";

            return (
              <ItemList.Item
                selected={isSelected}
                onClick={() => setSelectedEventId(id)}
              >
                <EventListContent eventType={entry.eventType} message={entry.message} timestamp={timestamp} actions={<> <div className="flex items-center gap-1">
                    {resolutionEnabled && entry.resolution?.taskId && entry.resolution.status && <StatusLabel status={entry.resolution.status} />}
                    <EventConnectorStatus eventType={getDisplayEventType(entry)} connectors={connectors} onSetConnector={openConnectorSetup} showSetConnector={false} />
                    <Chip
                      label={entry.status ?? "N/A"}
                      size="sm"
                      variant={getStatusChipVariant(entry.status)}
                    />
                  </div> </>} />
              </ItemList.Item>
            );
          }}
          onReachEnd={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          hasMore={Boolean(hasNextPage)}
          loadingMore={isFetchingNextPage}
        />
      </>
    );
  };

  const renderDetailsPanel = () => {
    if (!selectedEventId) {
      return (
        <div className="flex h-full min-h-0 flex-col bg-white">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-sm font-semibold">Event details</h2>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center px-8 pb-16 text-center">
            <MousePointer2 size={25} className="mb-4 text-textSecondary" />
            <p className="text-sm font-medium">Select an event</p>
            <p className="mt-2 max-w-56 text-xs leading-relaxed text-textSecondary">Review its message, facts and event data.</p>
          </div>
        </div>
      );
    }

    if (isFetchingEvent) {
      return (
        <LoadingStatus className="h-full" role="status" message="Loading event details…" />
      );
    }

    if (eventError) {
      return (
        <Typography
          variant="body2"
          color="error"
          style={{ padding: "1rem" }}
        >
          Error loading event details: {(eventError as Error).message || "Unknown error"}
        </Typography>
      );
    }

    if (!serverEventData) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
        >
          No event data available
        </Typography>
      );
    }

    const event = {
      ...entries.find((entry) => (entry.id || entry.eventRowKey) === selectedEventId),
      ...serverEventData,
    };
    return (
      <EventDetails
        compact={overview}
        showOpenProject={source !== "model"}
        event={{ ...event, status: event.status as any, eventType: getEventType(event.eventType) || event.eventType }}
        connectors={connectors}
        onSetConnector={openConnectorSetup}

      />
    );
  };

  const renderToolbarActions = () => (<>

              <EventTypesButton />
              {activeFilterCount > 0 && (
                <Chip
                  size="sm"
                  variant="info"
                  onClick={() => {
                    navigateView({ day: "", status: "", eventType: "", correlationId: "", start: "", end: "" });
                  }}
                >{`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""}`}</Chip>
              )}
              {(day || start || end) && (
                <span className="whitespace-nowrap text-sm text-textSecondary">
                  {day || "Custom range"}
                </span>
              )}
              <DeciPopover
                mode="click"
                placement="bottom-end"
                interactable
                visible={filtersOpen}
                onVisibleChange={(open) => {
                  if (open) openFilterPopover();
                  setFiltersOpen(open);
                }}
              >
                <DeciPopover.Reference asChild>
                  <Button
                    icon
                    size="sm"
                    label="Advanced filters"
                  >
                    <Icon name="filter-list" />
                  </Button>
                </DeciPopover.Reference>
                <DeciPopover.Content
                  asChild
                  handleVisibility={false}
                >
                  <FloatingPanel
                    open={filtersOpen}
                    placement="bottom-end"
                    unmountWhenHidden
                  >
                    <FormPanel className="w-[360px] max-w-[calc(100vw-2rem)] max-h-[75vh] overflow-y-auto p-4">
                      <FormPanel.Title>Advanced filters</FormPanel.Title>
                      <SelectField
                        name="event-time-range"
                        label="Time range"
                        value={filterTimeRangeType}
                        onValueChange={(value) => handleTimeRangeTypeChange(value as TimeRangeType)}
                        options={[
                          { value: "last24h", label: "Last 24 hours" },
                          { value: "lastHour", label: "Last hour" },
                          { value: "last5min", label: "Last 5 minutes" },
                          { value: "none", label: "No date filter" },
                          { value: "custom", label: "Custom range" },
                        ]}
                        searchable={false}
                        fullWidth
                      />
                      {(filterTimeRangeType === "none" || filterTimeRangeType === "custom") && (
                        <TextField
                          name="event-filter-day"
                          label="Day (UTC)"
                          type="date"
                          value={filterDay}
                          onValueChange={setFilterDay}
                          fullWidth
                        />
                      )}
                      {filterTimeRangeType === "custom" && (
                        <>
                          <TextField
                            name="event-filter-start"
                            label="Start time (UTC)"
                            type="datetime-local"
                            value={filterCustomStart}
                            onValueChange={setFilterCustomStart}
                            fullWidth
                          />
                          <TextField
                            name="event-filter-end"
                            label="End time (UTC)"
                            type="datetime-local"
                            value={filterCustomEnd}
                            onValueChange={setFilterCustomEnd}
                            helperText={filterDay ? `Times are limited to ${filterDay} in UTC.` : undefined}
                            fullWidth
                          />
                        </>
                      )}
                      <EventTypeFilter
                        value={filterEventType}
                        onValueChange={setFilterEventType}
                      />
                      <SelectField
                        name="event-filter-status"
                        label="Status"
                        value={filterStatus || "all"}
                        onValueChange={(value) =>
                          setFilterStatus(value === "all" ? "" : (value as typeof filterStatus))
                        }
                        options={[
                          { value: "all", label: "All" },
                          { value: "success", label: "Success" },
                          { value: "warning", label: "Warning" },
                          { value: "info", label: "Info" },
                          { value: "error", label: "Error" },
                          { value: "debug", label: "Debug" },
                        ]}
                        searchable={false}
                        fullWidth
                      />
                      <TextField
                        name="event-filter-correlation"
                        label="Correlation ID"
                        value={filterCorrelationId}
                        onValueChange={setFilterCorrelationId}
                        placeholder="Enter correlation ID"
                        fullWidth
                      />
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          label="Cancel"
                          size="sm"
                          variant="outline"
                          onClick={() => setFiltersOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          label="Apply"
                          size="sm"
                          intent="primary"
                          disabled={filterTimeRangeType === "custom" && (!filterCustomStart || !filterCustomEnd)}
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </Button>
                      </div>
                    </FormPanel>
                  </FloatingPanel>
                </DeciPopover.Content>
              </DeciPopover>
              <Button
                label="Refresh"
                size="sm"
                variant="outline"
                onClick={() => { void refetch(); if (overview) metricQueries.forEach(query => { void query.refetch(); }); }}
                disabled={isLoading}
              >
                <Icon name="refresh" />
                Refresh
              </Button>


  </>);

  if (source === "model" && requestedTab === "files") {
    const filesParams = new URLSearchParams(location.search);
    filesParams.delete("tab");
    return <Redirect to={`/monitor/files?${filesParams}`} />;
  }

  if (source === "model" && !modelId) {
    return (
      <div className="p-4">
        <Typography
          variant="body2"
          color="textSecondary"
        >
          Select a project to view model events.
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden min-h-0">
      {connectorSetupEvent && <EventConnectorDialog eventType={connectorSetupEvent} onClose={closeConnectorSetup} />}
      <div className="border-b border-border bg-white px-4 pt-3">
        <Tabs value={tab} onValueChange={value => navigateView({ tab: value, resolution: "", status: "" })}>
          <Tabs.List>
            <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
            <Tabs.Trigger value="events">Events</Tabs.Trigger>
          </Tabs.List>
        </Tabs>
      </div>
      {overview && (
        <section aria-label="Event overview" className="shrink-0 border-b border-border bg-white">
          <div className={`grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 ${resolutionEnabled ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
            {overviewCards.map((card, index) => (!resolutionEnabled && index === 1 ? null : (
              <Tooltip key={card.title}>
                <Tooltip.Trigger asChild>
                  <Button type="button" variant="ghost" label={card.title} onClick={card.open}
                    className="!h-auto min-w-0 !items-start !justify-start !gap-4 !whitespace-normal !rounded-lg border border-border !bg-white !px-4 !py-3 text-left hover:!bg-primary/5">
                    <card.icon size={25} className={`mt-1 shrink-0 ${card.tone}`} />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium">{card.title}</span>
                      {metricQueries[index].isLoading
                        ? <LoadingSpinner size="sm" className="my-2" role="status" aria-label={`Loading ${card.title}`} />
                        : <span className={`my-1 block font-semibold leading-8 ${card.value === "View history" || metricQueries[index].isError ? "text-base" : "text-2xl"}`}>{metricQueries[index].isError ? "Unavailable" : card.value}</span>}
                      <span className="block truncate text-xs font-normal text-textSecondary">{card.description}</span>
                    </span>
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content placement="bottom">
                  {index === 3 ? latestEvent?.message || card.description : "Counts respect the current filters. + indicates a lower bound while older history remains. View history means no matches were found in the first page, but older events are still available."}
                </Tooltip.Content>
              </Tooltip>
            )))}
          </div>
        </section>
      )}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-white px-4 py-2">
        {overview && <>
          <div className="w-[340px] max-w-full shrink-0">
            <TextField fullWidth aria-label="Search loaded events" placeholder="Search this list…" value={overviewSearch}
              onChange={event => setOverviewSearch(event.target.value)} />
          </div>
          {resolutionEnabled && <SelectField aria-label="Review state" value={resolution} onChange={event => navigateView({ resolution: event.target.value })}
            options={[{ value: "open", label: "Needs attention" }, { value: "resolved", label: "Resolved" }]} />}
        </>}
        <SelectField aria-label="Event group" value={group} onChange={event => navigateView({ group: event.target.value, eventType: "" })}
          options={[{ value: "", label: "All groups" }, ...Array.from(new Set(Object.values(EVENT_TYPE_INFO).map(info => info.category))).map(value => ({
            value, label: value === "fileProcessing" ? "File processing" : value === "orgmodels" ? "Organisation models" : value.charAt(0).toUpperCase() + value.slice(1),
          }))]} />
        <SwitchField name="event-show-debug" label="Show debug events" value={showDebug}
          onValueChange={(checked: boolean) => navigateView({ debug: checked ? "true" : "" })} />
        {overview && renderToolbarActions()}
      </div>
      <SplitPane className="flex-1 min-h-0">
        <SplitPane.CollapsiblePane
          minSize={320}
          preferredSize={overview ? "68%" : 440}
          collapsedLabel={title}
          persistId={`monitor-events-${source}-${overview ? "overview" : "history"}`}
        >
          <SplitPane.CollapsiblePane.Header className={`flex-wrap gap-2 ${overview ? "!px-5 !py-4" : ""}`}>
            <SplitPane.CollapsiblePane.Title>{title}{overview && entries.length > 0 && <span className="ml-3 text-xs font-normal text-textSecondary">{entries.length}{hasNextPage ? "+" : ""} events</span>}</SplitPane.CollapsiblePane.Title>
            {!overview && <SplitPane.CollapsiblePane.HeaderActions className="flex-wrap">
              {renderToolbarActions()}
              <SplitPane.CollapsiblePane.ToggleButton size="sm" />
            </SplitPane.CollapsiblePane.HeaderActions>}
          </SplitPane.CollapsiblePane.Header>
          <SplitPane.CollapsiblePane.Content className="!overflow-hidden bg-white">
            {overview ? renderOverviewTable() : renderEventsList()}
          </SplitPane.CollapsiblePane.Content>
        </SplitPane.CollapsiblePane>
        <SplitPane.Pane className="min-h-0 overflow-hidden bg-white">{renderDetailsPanel()}</SplitPane.Pane>
      </SplitPane>


    </div>
  );
};
